/* eslint-disable max-lines-per-function, max-statements */
import getServiceBatches from "helpers/getServiceBatches";
import useClearHomeVideoReviewsListButton from "hooks/actions/useClearHomeVideoReviewsListButton";
import useListButton from "hooks/actions/useListButton";
import { useCallback, useMemo, useState } from "react";
const HOME_VIDEO_REVIEW_LIST_BATCH_SIZE = 3;
/**
 * useHomeVideoReviewServiceListControls
 *
 * @description
 * Builds per-service Home Video Review list action props and coordinates the
 * global List All / Clear All Lists controls.
 *
 * @param {readonly HomeVideoReviewServiceListItem[]} serviceNames -
 * Home Video Review services used to build control props.
 *
 * @returns {UseHomeVideoReviewServiceListControls} -
 * List, clear, and global list action props.
 *
 */
const useHomeVideoReviewServiceListControls = (serviceNames) => {
    const [isClearAllListsLoading, setIsClearAllListsLoading] = useState(false);
    const [isListAllLoading, setIsListAllLoading] = useState(false);
    const isListControlsLoading = isClearAllListsLoading || isListAllLoading;
    const rawListButtonProps = serviceNames.reduce((acc, { serviceName }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        acc[serviceName] = useListButton({
            serviceName,
            serviceType: "reviews"
        });
        // Accumulate per-service list button props
        return acc;
    }, {});
    // Disable list buttons globally while any list operation is in progress
    const listButtonProps = useMemo(() => serviceNames.reduce((acc, { serviceName }) => {
        const listButton = rawListButtonProps[serviceName];
        acc[serviceName] = {
            ...listButton,
            isDisabled: listButton.isDisabled || isListControlsLoading
        };
        return acc;
    }, {}), [isListControlsLoading, serviceNames, rawListButtonProps]);
    // Build per-service clear button props, disabling while any operation is in progress
    const clearListButtonProps = useMemo(() => serviceNames.reduce((acc, { serviceName }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        acc[serviceName] = useClearHomeVideoReviewsListButton({
            isDisabled: isListControlsLoading,
            isLoading: rawListButtonProps[serviceName].loading,
            serviceName
        });
        return acc;
    }, {}), [isListControlsLoading, rawListButtonProps, serviceNames]);
    // Collect services that are eligible for listing (not disabled)
    const servicesToList = useMemo(() => serviceNames
        .map(({ serviceName }) => serviceName)
        .filter((serviceName) => !rawListButtonProps[serviceName].isDisabled), [serviceNames, rawListButtonProps]);
    // Batch services into groups of REVIEWS_LIST_BATCH_SIZE for sequential listing
    const serviceBatches = useMemo(() => getServiceBatches(servicesToList, HOME_VIDEO_REVIEW_LIST_BATCH_SIZE), [servicesToList]);
    // Collect services that are eligible for clearing (not disabled)
    const servicesToClear = useMemo(() => serviceNames
        .map(({ serviceName }) => serviceName)
        .filter((serviceName) => !clearListButtonProps[serviceName].isDisabled), [clearListButtonProps, serviceNames]);
    // Batch services into groups of REVIEWS_LIST_BATCH_SIZE for sequential clearing
    const clearServiceBatches = useMemo(() => getServiceBatches(servicesToClear, HOME_VIDEO_REVIEW_LIST_BATCH_SIZE), [servicesToClear]);
    // Execute list all: chains service batches sequentially, parallelizes within each batch
    const onListAll = useCallback(() => {
        if (isListControlsLoading || !servicesToList.length) {
            return;
        }
        setIsListAllLoading(true);
        const listAllPromise = serviceBatches.reduce((chain, serviceBatch) => chain.then(() => Promise.allSettled(serviceBatch.map((serviceName) => Promise.resolve(rawListButtonProps[serviceName].onClick()))).then(() => undefined)), Promise.resolve());
        listAllPromise.finally(() => {
            setIsListAllLoading(false);
        });
    }, [
        isListControlsLoading,
        rawListButtonProps,
        serviceBatches,
        servicesToList.length
    ]);
    // Execute clear all: chains service batches sequentially, parallelizes within each batch
    const onClearAllLists = useCallback(() => {
        if (isListControlsLoading || !servicesToClear.length) {
            return;
        }
        setIsClearAllListsLoading(true);
        const clearAllPromise = clearServiceBatches.reduce((chain, serviceBatch) => chain.then(() => Promise.allSettled(serviceBatch.map((serviceName) => Promise.resolve(clearListButtonProps[serviceName].onClick()))).then(() => undefined)), Promise.resolve());
        clearAllPromise.finally(() => {
            setIsClearAllListsLoading(false);
        });
    }, [
        clearListButtonProps,
        clearServiceBatches,
        isListControlsLoading,
        servicesToClear.length
    ]);
    // Global "List All" button configuration
    const listAllButtonProps = useMemo(() => ({
        isDisabled: isListControlsLoading || !servicesToList.length,
        label: "List All",
        loading: isListAllLoading,
        onClick: onListAll
    }), [isListAllLoading, isListControlsLoading, onListAll, servicesToList.length]);
    // Global "Clear All Lists" button configuration
    const clearAllListsButtonProps = useMemo(() => ({
        isDisabled: isListControlsLoading || !servicesToClear.length,
        label: "Clear All Lists",
        loading: isClearAllListsLoading,
        onClick: onClearAllLists,
        secondary: true
    }), [
        isClearAllListsLoading,
        isListControlsLoading,
        onClearAllLists,
        servicesToClear.length
    ]);
    return {
        clearAllListsButtonProps,
        clearListButtonProps,
        isListAllLoading,
        listAllButtonProps,
        listButtonProps
    };
};
export default useHomeVideoReviewServiceListControls;
